import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, CheckCircle2, XCircle, Clock, Smartphone, Crown,
  RefreshCw, ArrowRight, Building2, AlertTriangle, Wifi, Shield
} from "lucide-react";
import subscriptionApi from "../../api/subscription";

// ─── Constants ─────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 3000;       // 3s — optimized for sandbox
const MAX_ATTEMPTS = 24;             // 72s total
const COUNTDOWN_SECONDS = 90;       // 90s user-facing timer
const BACKOFF_AFTER = 10;           // after 10 attempts, slow down

// ─── Utility ──────────────────────────────────────────────────────────────────
const formatCountdown = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// ─── Sub-components ────────────────────────────────────────────────────────────
const PhonePulse = () => (
  <div className="relative flex items-center justify-center w-24 h-24 mx-auto">
    <span className="absolute w-24 h-24 rounded-full bg-indigo-400 opacity-20 animate-ping" />
    <span className="absolute w-16 h-16 rounded-full bg-indigo-400 opacity-30 animate-ping" style={{ animationDelay: "0.3s" }} />
    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-xl shadow-indigo-300">
      <Smartphone className="w-9 h-9 text-white" />
    </div>
  </div>
);

const IpnWaiting = () => (
  <div className="relative flex items-center justify-center w-24 h-24 mx-auto">
    <span className="absolute w-24 h-24 rounded-full bg-blue-400 opacity-20 animate-ping" style={{ animationDuration: "2s" }} />
    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-xl shadow-blue-300">
      <Building2 className="w-9 h-9 text-white" />
    </div>
  </div>
);

const SuccessIcon = () => (
  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto shadow-xl shadow-emerald-200">
    <CheckCircle2 className="w-12 h-12 text-white" />
  </div>
);

const FailIcon = ({ isTimeout }) => (
  <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto shadow-xl ${isTimeout ? "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-200" : "bg-gradient-to-br from-red-400 to-red-600 shadow-red-200"}`}>
    {isTimeout ? <Clock className="w-12 h-12 text-white" /> : <XCircle className="w-12 h-12 text-white" />}
  </div>
);

const StepBadge = ({ num, text, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-indigo-100 text-indigo-600"}`}>
      {done ? <CheckCircle2 className="w-4 h-4" /> : num}
    </div>
    <span className={`text-sm ${done ? "text-emerald-700 line-through decoration-emerald-400" : "text-gray-700"}`}>{text}</span>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const SubscriptionPendingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [manualMessage, setManualMessage] = useState("");
  const [dots, setDots] = useState(".");

  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const attemptsRef = useRef(0);

  const {
    plan,
    paymentMethod,
    kcbChannel,
    checkoutRequestId,
    reference,
    sourceBank,
    amount,
    subscriptionId,
    customerMessage,
  } = location.state || {};

  const isIpnChannel = paymentMethod === "kcb" && kcbChannel !== "mpesa_to_kcb";
  const isStkChannel = paymentMethod === "daraja" || (paymentMethod === "kcb" && kcbChannel === "mpesa_to_kcb");

  // ─── Animated dots ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (paymentStatus !== "pending") return;
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "." : d + "."));
    }, 600);
    return () => clearInterval(interval);
  }, [paymentStatus]);

  // ─── Stop polling helper ───────────────────────────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  // ─── Manual verify & Sandbox Simulation ───────────────────────────────────────
  const handleVerifyPayment = async () => {
    try {
      setVerifying(true);
      setManualMessage("");
      const res = await subscriptionApi.queryStkStatus(checkoutRequestId);
      if (res.data?.status === "completed") {
        stopPolling();
        setPaymentStatus("completed");
        setTimeout(() => {
          navigate("/subscription/success", {
            state: {
              plan: res.data.subscription?.plan || plan,
              subscription: res.data.subscription,
              transactionId: checkoutRequestId,
            },
          });
        }, 800);
      } else if (res.data?.status === "failed") {
        stopPolling();
        setPaymentStatus("failed");
      } else {
        setManualMessage("Payment still pending. If you've entered your PIN, please wait a few more seconds.");
      }
    } catch (err) {
      setManualMessage("Could not verify at this moment. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleSimulateSandboxApproval = async () => {
    try {
      setVerifying(true);
      setManualMessage("Simulating M-Pesa PIN approval in Sandbox mode...");
      await subscriptionApi.testKcbCallback({
        Body: {
          stkCallback: {
            CheckoutRequestID: checkoutRequestId || reference,
            ResultCode: 0,
            ResultDesc: "The service request is processed successfully."
          }
        }
      });
      await pollTick();
    } catch (err) {
      setManualMessage("Simulation note: " + (err.response?.data?.error || err.message));
    } finally {
      setVerifying(false);
    }
  };

  // ─── Polling Effect ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!checkoutRequestId && !isIpnChannel) {
      navigate("/pricing");
      return;
    }

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setPaymentStatus("timeout");
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // For IPN channels (interbank), we poll subscription status only
    // For STK channels, we poll both subscription status AND STK query
    pollRef.current = setInterval(async () => {
      attemptsRef.current += 1;
      setPollingAttempts(attemptsRef.current);

      // Slow down after BACKOFF_AFTER attempts
      if (attemptsRef.current === BACKOFF_AFTER && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          attemptsRef.current += 1;
          setPollingAttempts(attemptsRef.current);
          await pollTick();
          if (attemptsRef.current >= MAX_ATTEMPTS) {
            stopPolling();
            setPaymentStatus((prev) => prev === "pending" ? "timeout" : prev);
          }
        }, 5000); // slower: 5s after backoff
      }

      await pollTick();

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        stopPolling();
        setPaymentStatus((prev) => (prev === "pending" ? "timeout" : prev));
      }
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [checkoutRequestId, isIpnChannel]);

  const pollTick = async () => {
    try {
      // 1. Check if subscription is already active
      const subRes = await subscriptionApi.getMySubscription();
      if (subRes.data?.subscription?.status === "active") {
        stopPolling();
        setActiveSubscription(subRes.data);
        setPaymentStatus("completed");
        setTimeout(() => {
          navigate("/subscription/success", {
            state: {
              plan: subRes.data.subscription?.plan || plan,
              subscription: subRes.data,
              transactionId: checkoutRequestId,
            },
          });
        }, 1200);
        return;
      }

      // 2. For STK channels: also query STK status
      if (isStkChannel && checkoutRequestId) {
        const stkRes = await subscriptionApi.queryStkStatus(checkoutRequestId);
        const stkStatus = stkRes.data?.status;

        if (stkStatus === "completed") {
          stopPolling();
          setPaymentStatus("completed");
          setTimeout(() => {
            navigate("/subscription/success", {
              state: {
                plan: stkRes.data.subscription?.plan || plan,
                subscription: stkRes.data.subscription,
                transactionId: checkoutRequestId,
              },
            });
          }, 1200);
        } else if (stkStatus === "failed") {
          stopPolling();
          setPaymentStatus("failed");
        }
      }
    } catch (_) {
      // Silently ignore network errors — user may have spotty connectivity
    }
  };

  // ─── Status Content ────────────────────────────────────────────────────────
  const getStatusContent = () => {
    if (paymentStatus === "completed") return {
      icon: <SuccessIcon />,
      title: "Payment Successful!",
      badge: { text: "Activated", color: "emerald" },
      subtitle: activeSubscription?.subscription?.plan?.name
        ? `${activeSubscription.subscription.plan.name} is now active.`
        : "Your subscription has been activated successfully.",
    };
    if (paymentStatus === "failed") return {
      icon: <FailIcon />,
      title: "Payment Not Completed",
      badge: { text: "Declined", color: "red" },
      subtitle: "The M-Pesa request was cancelled or PIN was not entered. Please try again.",
    };
    if (paymentStatus === "timeout") return {
      icon: <FailIcon isTimeout />,
      title: "Request Expired",
      badge: { text: "Timed Out", color: "amber" },
      subtitle: "The payment window has closed. Please initiate a new payment.",
    };
    return {
      icon: isIpnChannel ? <IpnWaiting /> : <PhonePulse />,
      title: isIpnChannel ? `Awaiting Bank Transfer${dots}` : `Waiting for M-Pesa PIN${dots}`,
      badge: { text: "Pending", color: "indigo" },
      subtitle: isIpnChannel
        ? "Complete your bank transfer using the reference shown. Our system will detect the payment automatically."
        : customerMessage || "Check your phone and enter your M-Pesa PIN to complete payment.",
    };
  };

  const { icon, title, badge, subtitle } = getStatusContent();

  const badgeColors = {
    emerald: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm border border-gray-200 mb-4">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-semibold text-gray-600">Secure Payment</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-7 text-center">
          {/* Icon */}
          <div className="mb-5">{icon}</div>

          {/* Badge */}
          <div className="flex justify-center mb-3">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${badgeColors[badge.color]}`}>
              {badge.text}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-black text-gray-900 mb-2">{title}</h1>
          <p className="text-gray-500 text-sm mb-5 max-w-xs mx-auto leading-relaxed">{subtitle}</p>

          {/* STK Instructions */}
          {paymentStatus === "pending" && isStkChannel && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 mb-5 text-left">
              <h3 className="text-sm font-bold text-indigo-800 mb-3 flex items-center gap-2">
                <Smartphone className="w-4 h-4" /> M-Pesa Steps
              </h3>
              <div className="space-y-2.5">
                <StepBadge num="1" text="Check your phone — an M-Pesa prompt should appear" done={pollingAttempts > 2} />
                <StepBadge num="2" text="Enter your M-Pesa PIN when prompted" done={false} />
                <StepBadge num="3" text="Wait for confirmation — do not close this page" done={false} />
              </div>
            </div>
          )}

          {/* IPN Instructions */}
          {paymentStatus === "pending" && isIpnChannel && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-5 text-left space-y-3">
              <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" /> Interbank Transfer Details
              </h3>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Money has NOT been deducted yet.</strong> Please open your bank app ({sourceBank || "your bank"}) and send the payment using the details below.
                </span>
              </div>

              <div className="bg-white rounded-xl p-3 border border-blue-100 space-y-2 text-xs font-mono">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-sans">KCB Paybill / Code:</span>
                  <span className="font-bold text-gray-800">522522</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-sans">Payment Reference:</span>
                  <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                    {reference || checkoutRequestId || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-sans">Amount:</span>
                  <span className="font-bold text-emerald-700">KES {Number(amount || plan?.price || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <StepBadge num="1" text={`Open ${sourceBank || "your bank"} app or PesaLink / EFT`} done={false} />
                <StepBadge num="2" text={`Transfer KES ${Number(amount || plan?.price || 0).toLocaleString()} to Paybill 522522`} done={false} />
                <StepBadge num="3" text={`Set Account Reference to: ${reference || checkoutRequestId || "SUB-..."}`} done={false} />
                <StepBadge num="4" text="Our system will detect the payment automatically upon KCB IPN callback." done={false} />
              </div>
            </div>
          )}

          {/* Plan Info */}
          {plan && paymentStatus === "pending" && (
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 mb-5">
              <div className="text-left">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Plan</p>
                <p className="font-bold text-gray-800">{plan.name}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Amount</p>
                <p className="font-black text-indigo-700">KES {Number(plan.price).toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Countdown + progress bar */}
          {paymentStatus === "pending" && (
            <div className="mb-5">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
                <span className="flex items-center gap-1">
                  <Wifi className="w-3 h-3" /> Checking status every {pollingAttempts > BACKOFF_AFTER ? 5 : 3}s
                </span>
                <span className="font-mono font-medium text-gray-600">
                  {formatCountdown(countdown)} remaining
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-1.5 rounded-full transition-all duration-1000 bg-gradient-to-r from-indigo-400 to-indigo-600"
                  style={{ width: `${(countdown / COUNTDOWN_SECONDS) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Activated Plan */}
          {activeSubscription?.subscription && (
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-4 mb-5">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-emerald-600" />
                <span className="font-bold text-emerald-800">Subscription Activated!</span>
              </div>
              <p className="text-lg font-black text-gray-900">{activeSubscription.subscription.plan?.name}</p>
              <p className="text-gray-500 text-sm">
                Valid until {new Date(activeSubscription.subscription.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          )}

          {/* Manual verify feedback */}
          {manualMessage && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-left">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">{manualMessage}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2.5">
            {paymentStatus === "pending" && (
              <button
                onClick={() => navigate("/pricing")}
                className="w-full px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel Payment
              </button>
            )}

            {(paymentStatus === "failed" || paymentStatus === "timeout") && (
              <div className="space-y-2">
                <button
                  onClick={() => navigate("/pricing/payment", { state: { plan } })}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-md shadow-indigo-200 transition-all"
                >
                  Try Again <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  className="w-full px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                >
                  Back to Plans
                </button>
              </div>
            )}

            {paymentStatus === "completed" && (
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-200 transition-all"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Reference Footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-gray-400">
            Reference: <span className="font-mono">{String(checkoutRequestId || "—").slice(-12)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPendingPage;